import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import { logDispatchEvent } from "./dispatchLog";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import FitWord from "../FitWord";
import type { DeskInfo } from "../../types/Dispatch";
import { useDemoVisible } from "../helpers/demoMode";
import { recoverScanCaseReported } from "../helpers/scanCase";
import { apiFetch } from "../../api/apiFetch";
import DispatchSessionBar from "./dispatch-session-bar";
import "../../styles/dispatch-bench.css";

type Props = {
    onDeskScanned: (desk: DeskInfo) => void;
    goBack: () => void;
};

const DispatchScanDesk = ({ onDeskScanned, goBack }: Props) => {
    const { t } = useTranslation();
    const demo = useDemoVisible();
    const [scan, setScan] = useState("");
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [demoDesks, setDemoDesks] = useState<Array<{ name: string; barcode: string }>>([]);

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "Newport" };

    // Fetch real desks for the operator's warehouse so demo buttons hit
    // valid DB barcodes. The endpoint returns { desks: [{ name, barcode }] }
    // for the warehouse passed in. We show up to 4 to keep the demo panel
    // tidy.
    useEffect(() => {
        if (!demo || !userObject.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/dispatch-admin/desks?warehouse=${encodeURIComponent(userObject.warehouse)}`,
                    {},
                );
                if (!res.ok || !alive) return;
                const data: { desks?: Array<{ name: string; barcode: string }> } = await res.json();
                const list = (data.desks ?? []).slice(0, 4);
                if (alive) setDemoDesks(list);
            } catch {
                /* leave empty — demo panel just hides itself */
            }
        })();
        return () => { alive = false; };
    }, [demo, userObject.warehouse]);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 500);
    };

    // Local-first validation: the full desk fleet (a handful of rows,
    // all warehouses) is prefetched on mount so a desk scan resolves
    // with ZERO network wait. The /desk/check round trip below remains
    // the fallback for a barcode not in the (possibly still-loading)
    // list. Barcodes compare case-sensitively, as everywhere.
    const [allDesks, setAllDesks] = useState<Array<{ barcode: string; name: string | null; warehouse: string }>>([]);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/dispatch-admin/desks/all`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && Array.isArray(data.desks)) setAllDesks(data.desks);
            } catch { /* fall back to per-scan check */ }
        })();
        return () => { alive = false; };
    }, []);

    // Hits /dispatch-admin/desk/check — server returns the warehouse the desk
    // belongs to so DeskInfo's warehouse comes from the desk record (admin-
    // configured) rather than the operator's session profile.
    const handleSubmit = useCallback(async (scannedValue?: string) => {
        const raw = (scannedValue ?? scan).trim();
        if (!raw) return;
        setScan("");
        // A scanner that lags SHIFT delivers "pemDESK6" for "PEMDESK6" and
        // the operator can't get onto the bench at all. The desk fleet is a
        // handful of rows already in memory, so recover the case against it
        // — unambiguously, or not at all. See scanCase.ts.
        const val = recoverScanCaseReported(raw, allDesks.map(d => d.barcode), "Dispatch").value;
        const local = allDesks.find(d => d.barcode === val);
        if (local) {
            playSuccess();
            flashScanStatus("success");
            logDispatchEvent({ event: "desk-scan", barcode: val, desk_id: val, result: "ok" });
            onDeskScanned({ deskId: val, warehouse: local.warehouse });
            return;
        }
        try {
            const res = await apiFetch(`/dispatch-admin/desk/check`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: val }),
            });
            if (!res.ok) {
                playError();
                flashScanStatus("error");
                toast.error(t("server_error"), { toastId: "desk-check-err" });
                return;
            }
            const data = await res.json();
            if (!data.valid) {
                playError();
                flashScanStatus("error");
                toast.error(t("desk_unknown"), { toastId: "desk-unknown" });
                logDispatchEvent({ event: "desk-scan", barcode: val, desk_id: val, result: "fail-unknown" });
                return;
            }
            playSuccess();
            flashScanStatus("success");
            logDispatchEvent({ event: "desk-scan", barcode: val, desk_id: val, result: "ok" });
            onDeskScanned({
                deskId: val,
                warehouse: data.warehouse,
            });
        } catch {
            playError();
            flashScanStatus("error");
            toast.error(t("server_error"), { toastId: "desk-check-net-err" });
        }
    }, [scan, allDesks, onDeskScanned, t]);

    return (
        <div
            className="scan-panel dispatch-scan dispatch-bench"
            style={{
                backgroundColor:
                    scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" :
                    undefined,
            }}
        >
                <ScanFlashOverlay active={scanStatus === "error"} />
            <DispatchSessionBar onBack={goBack} />

            <div className="scan-centre">
                <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                    {t("scan_desk").toUpperCase().split(" ").map((word, i) => (
                        <FitWord key={i}>{word}</FitWord>
                    ))}
                </div>
            </div>

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={false}
                disableCamera
            />

            {/* Demo — toggled via Admin Settings → Demo Mode. Pulls real
                desk barcodes from /dispatch-admin/desks; falls back to
                hardcoded examples if no desks are configured yet. */}
            {demo && demoDesks.length > 0 && (
                <div className="dispatch-demo-panel">
                    <span className="dispatch-demo-label">Demo</span>
                    {demoDesks.map(d => (
                        <button
                            key={d.barcode}
                            className="dispatch-demo-btn"
                            onClick={() => handleSubmit(d.barcode)}
                        >
                            {d.barcode}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DispatchScanDesk;
